import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, FileText, Users, Link as LinkIcon, KeyRound,
         Copy, Check, Loader2, ShieldOff, ExternalLink, ClipboardEdit,
         Zap, X, BookOpen, LineChart } from "lucide-react";
import HistoricalInvoiceModal from "@/components/HistoricalInvoiceModal";
import ClientLedgerDrawer from "@/components/ClientLedgerDrawer";

export default function Clients() {
    const nav = useNavigate();
    const [list, setList] = useState([]);            // aggregated from invoices
    const [portalList, setPortalList] = useState([]); // clients collection docs
    const [q, setQ] = useState("");
    const [portalPanelFor, setPortalPanelFor] = useState(null);
    const [historyModal, setHistoryModal] = useState(null); // client doc
    const [bulkModal, setBulkModal] = useState(false);
    const [ledgerFor, setLedgerFor] = useState(null);

    const load = async () => {
        const [inv, ports] = await Promise.all([
            api.get("/clients", { params: { q } }),
            api.get("/clients/portal"),
        ]);
        setList(inv.data);
        setPortalList(ports.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

    const portalFor = (buyer) => {
        // Try GSTIN match first, else case-insensitive name
        const g = (buyer.gstin || "").toUpperCase();
        return portalList.find(p => (g && p.gstin?.toUpperCase() === g)
                                 || p.name?.toLowerCase() === (buyer.name || "").toLowerCase());
    };

    const newInvoice = (c) => {
        const params = new URLSearchParams({
            name: c.buyer.name || "",
            gstin: c.buyer.gstin || "",
        });
        nav(`/invoices/new?${params.toString()}`);
    };

    return (
        <div data-testid="clients-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Clients</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Directory + client-portal access management (pilot: Rulen &amp; Cambrian Hall)
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="hdb-card px-4 py-2 flex items-center gap-2">
                        <Users size={14} className="text-slate-500" />
                        <span className="hdb-num text-sm font-bold text-slate-900">{list.length}</span>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">Clients</span>
                    </div>
                    <div className="hdb-card px-4 py-2 flex items-center gap-2 bg-emerald-50/60">
                        <LinkIcon size={14} className="text-emerald-700" />
                        <span className="hdb-num text-sm font-bold text-emerald-800">
                            {portalList.filter(p => p.portal_enabled).length}
                        </span>
                        <span className="text-xs text-emerald-700 uppercase tracking-wider">Portal-enabled</span>
                    </div>
                    <button onClick={() => setBulkModal(true)}
                            className="hdb-btn bg-indigo-600 text-white hover:bg-indigo-700"
                            data-testid="bulk-enable-portal-btn">
                        <Zap size={13}/> Bulk-enable portal
                    </button>
                </div>
            </div>

            <div className="hdb-card mb-5 p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                    <input className="hdb-input pl-9" placeholder="Search by name or GSTIN…"
                           value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search" />
                </div>
            </div>

            <div className="hdb-card overflow-hidden">
                <table className="hdb-table w-full">
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>GSTIN / State</th>
                            <th>Portal</th>
                            <th className="text-right">Invoices</th>
                            <th className="text-right">Last Order</th>
                            <th className="text-right">Lifetime Revenue</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((c, i) => {
                            const portal = portalFor(c.buyer);
                            return (
                                <tr key={i} data-testid={`client-row-${c.buyer.name}`}>
                                    <td>
                                        <div className="font-semibold text-slate-900">{c.buyer.name}</div>
                                        {c.buyer.address && <div className="text-xs text-slate-500 mt-0.5">{c.buyer.address}</div>}
                                    </td>
                                    <td>
                                        {c.buyer.gstin && <div className="hdb-mono text-xs">{c.buyer.gstin}</div>}
                                        <div className="text-xs text-slate-500">
                                            {c.buyer.state || "—"} {c.buyer.state_code && `(${c.buyer.state_code})`}
                                        </div>
                                    </td>
                                    <td>
                                        {portal?.portal_enabled ? (
                                            <button className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                    onClick={() => setPortalPanelFor({ ...c, portal })}
                                                    data-testid={`portal-manage-${i}`}>
                                                <LinkIcon size={11}/> Enabled · Manage
                                            </button>
                                        ) : (
                                            <button className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                                                    onClick={() => setPortalPanelFor({ ...c, portal: null })}
                                                    data-testid={`portal-enable-${i}`}>
                                                <KeyRound size={11}/> Enable portal
                                            </button>
                                        )}
                                    </td>
                                    <td className="hdb-num hdb-num-right">{c.invoice_count}</td>
                                    <td className="hdb-mono hdb-num-right text-slate-600">{c.last_order_date}</td>
                                    <td className="hdb-num hdb-num-right font-bold">{fmt(c.total_revenue)}</td>
                                    <td className="text-right whitespace-nowrap">
                                        <button className="hdb-btn hdb-btn-ghost mr-1" onClick={() => setLedgerFor(c)}
                                                data-testid={`ledger-btn-${c.buyer.name}`}
                                                title="Account ledger + Mark paid/unpaid">
                                            <BookOpen size={13} /> Ledger
                                        </button>
                                        <Link className="hdb-btn hdb-btn-ghost mr-1"
                                              to={`/clients/rate-card?name=${encodeURIComponent(c.buyer.name || "")}&gstin=${encodeURIComponent(c.buyer.gstin || "")}`}
                                              data-testid={`ratecard-btn-${c.buyer.name}`}
                                              title="Per-client rate card vs current master prices">
                                            <LineChart size={13} /> Rate card
                                        </Link>
                                        <button className="hdb-btn hdb-btn-primary" onClick={() => newInvoice(c)}
                                                data-testid={`new-inv-for-${c.buyer.name}`}>
                                            <Plus size={13} /> Invoice
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {list.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-12 text-slate-400">
                                No clients yet — create your first invoice to build the directory.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {portalPanelFor && (
                <PortalManagePanel row={portalPanelFor}
                                   onClose={() => setPortalPanelFor(null)}
                                   onSaved={load}
                                   onOpenHistorical={(c) => { setPortalPanelFor(null); setHistoryModal(c); }} />
            )}
            {historyModal && (
                <HistoricalInvoiceModal client={historyModal}
                                        onClose={() => setHistoryModal(null)}
                                        onSaved={() => { setHistoryModal(null); load(); }} />
            )}
            {ledgerFor && (
                <ClientLedgerDrawer client={ledgerFor} onClose={() => setLedgerFor(null)} />
            )}
            {bulkModal && (
                <BulkEnablePortalModal onClose={() => setBulkModal(false)}
                                       onDone={() => { setBulkModal(false); load(); }} />
            )}
        </div>
    );
}


/* --------- Bulk-enable portal for every client with ≥ 1 invoice --------- */
function BulkEnablePortalModal({ onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.post("/clients/portal/bulk-enable", { dry_run: true });
                setPreview(r.data);
            } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
        })();
    }, []);

    const run = async () => {
        setRunning(true);
        try {
            const r = await api.post("/clients/portal/bulk-enable", { dry_run: false });
            setResult(r.data);
            toast.success(`Enabled ${r.data.totals.created + r.data.totals.enabled} clients · already-enabled ${r.data.totals.already_enabled}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setRunning(false); }
    };

    const copyAll = () => {
        if (!result) return;
        const lines = [
            ...(result.created || []).map(c => `${c.name}${c.gstin ? " · " + c.gstin : ""}\n${c.portal_link}`),
            ...(result.enabled || []).map(c => `${c.name}${c.gstin ? " · " + c.gstin : ""}\n${c.portal_link}`),
        ].join("\n\n");
        navigator.clipboard.writeText(lines);
        toast.success("Copied all portal links to clipboard");
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
             onClick={onClose} data-testid="bulk-enable-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-200 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-700 grid place-items-center">
                        <Zap size={18}/>
                    </div>
                    <div className="flex-1">
                        <h2 className="text-lg font-black hdb-heading">Bulk-enable Portal</h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Enables client portal + magic-link access for every client
                            with at least one invoice on file. Existing portal-enabled
                            clients are untouched.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"
                            data-testid="bulk-enable-close"><X size={18}/></button>
                </div>

                {result ? (
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <StatCard label="Created" value={result.totals.created} color="emerald" />
                            <StatCard label="Enabled" value={result.totals.enabled} color="indigo" />
                            <StatCard label="Already on" value={result.totals.already_enabled} color="slate" />
                        </div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Portal links · share with your clients
                            </div>
                            <button onClick={copyAll} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
                                    data-testid="bulk-copy-all">
                                <Copy size={12}/> Copy all
                            </button>
                        </div>
                        <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                            {[...(result.created || []), ...(result.enabled || [])].map(c => (
                                <div key={c.id} className="p-2 bg-slate-50 rounded-lg text-xs border border-slate-200">
                                    <div className="font-bold text-slate-900">{c.name}
                                        {c.gstin && <span className="ml-2 font-mono text-[10px] text-slate-500">{c.gstin}</span>}
                                        <span className="ml-2 text-[10px] text-slate-400">· {c.invoice_count} invoices</span>
                                    </div>
                                    <div className="font-mono text-[11px] text-indigo-700 break-all">{c.portal_link}</div>
                                </div>
                            ))}
                            {(result.created?.length + result.enabled?.length) === 0 && (
                                <div className="text-center text-slate-400 text-sm py-6">
                                    Nothing to do — all clients are already portal-enabled.
                                </div>
                            )}
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button onClick={onDone} className="hdb-btn hdb-btn-primary"
                                    data-testid="bulk-done">
                                <Check size={13}/> Done
                            </button>
                        </div>
                    </div>
                ) : preview === null ? (
                    <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
                        <Loader2 size={16} className="animate-spin"/> Analyzing invoice buyers…
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="grid grid-cols-3 gap-3 mb-3">
                            <StatCard label="To create" value={preview.totals.to_create} color="emerald" />
                            <StatCard label="To enable" value={preview.totals.to_enable} color="indigo" />
                            <StatCard label="Already on" value={preview.totals.already_enabled} color="slate" />
                        </div>
                        <div className="text-[11px] text-slate-500 mb-3">
                            {preview.unique_buyers} unique buyers found across invoices.
                            {preview.totals.to_create > 0 && (
                                <> · <span className="font-semibold text-slate-700">{preview.totals.to_create}</span> new client records will be created from invoice buyer info.</>
                            )}
                        </div>
                        <div className="border rounded-lg overflow-hidden max-h-[40vh] overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-100 text-slate-500 uppercase tracking-wider text-[10px]">
                                    <tr><th className="text-left px-2 py-1.5">Client</th>
                                        <th className="text-left px-2 py-1.5">GSTIN</th>
                                        <th className="text-right px-2 py-1.5">Invoices</th>
                                        <th className="text-right px-2 py-1.5">Action</th></tr>
                                </thead>
                                <tbody>
                                    {[
                                        ...(preview.to_create || []).map(c => ({...c, action: "create"})),
                                        ...(preview.to_enable || []).map(c => ({...c, action: "enable"})),
                                    ].map((c, i) => (
                                        <tr key={i} className="border-t border-slate-100">
                                            <td className="px-2 py-1.5 font-semibold">{c.name}</td>
                                            <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{c.gstin || "—"}</td>
                                            <td className="px-2 py-1.5 text-right">{c.invoice_count}</td>
                                            <td className="px-2 py-1.5 text-right">
                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase
                                                    ${c.action === "create" ? "bg-emerald-100 text-emerald-800" : "bg-indigo-100 text-indigo-800"}`}>
                                                    {c.action}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {preview.totals.to_create + preview.totals.to_enable === 0 && (
                                        <tr><td colSpan={4} className="text-center py-6 text-slate-400">
                                            Everyone is already portal-enabled — nothing to do.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={onClose} className="hdb-btn bg-slate-100 hover:bg-slate-200"
                                    data-testid="bulk-cancel">Cancel</button>
                            <button onClick={run}
                                    disabled={running || (preview.totals.to_create + preview.totals.to_enable === 0)}
                                    className={`hdb-btn hdb-btn-primary ${running || (preview.totals.to_create + preview.totals.to_enable === 0) ? "opacity-40 cursor-not-allowed" : ""}`}
                                    data-testid="bulk-confirm">
                                {running ? <><Loader2 size={13} className="animate-spin"/> Enabling…</>
                                         : <><Zap size={13}/> Enable {preview.totals.to_create + preview.totals.to_enable} clients</>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, color }) {
    const cls = {
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
        indigo:  "bg-indigo-50 border-indigo-200 text-indigo-800",
        slate:   "bg-slate-50 border-slate-200 text-slate-700",
    }[color] || "bg-slate-50 border-slate-200 text-slate-700";
    return (
        <div className={`border rounded-lg p-3 ${cls}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
            <div className="text-2xl font-black hdb-num mt-1">{value}</div>
        </div>
    );
}

/* --------- Portal management side-panel (enable / rotate / disable / settings) --------- */
function PortalManagePanel({ row, onClose, onSaved, onOpenHistorical }) {
    // Prop `row.portal` may be null when opened for a not-yet-enabled client.
    // We mirror it in local state so that after enable/rotate/settings-save the
    // same modal immediately shows the link box + Rotate/Disable/Add-historical
    // buttons WITHOUT the user having to close and reopen.
    const [portal, setPortal] = useState(row.portal || null);
    const buyer = row.buyer;
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [link, setLink] = useState(portal?.portal_enabled ? portal.portal_link : "");
    const [contactEmail, setContactEmail] = useState(portal?.contact_email || "");
    const [contactPhone, setContactPhone] = useState(portal?.contact_phone || "");
    const [cycle, setCycle] = useState(portal?.reminder_cycle_days || 30);
    const enabled = !!portal?.portal_enabled;   // local, updates after enableOrCreate
    const clientId = portal?.id;

    const enableOrCreate = async () => {
        setBusy(true);
        try {
            const r = await api.post("/clients/portal/enable", {
                name: buyer.name, gstin: buyer.gstin || "",
                address: buyer.address || "", state: buyer.state || "",
                state_code: buyer.state_code || "",
                contact_email: contactEmail, contact_phone: contactPhone,
                reminder_cycle_days: parseInt(cycle) || 30,
            });
            setLink(r.data.portal_link);
            // Update local portal state so the modal flips into "enabled" mode
            // without needing a close+reopen. Includes the id required for
            // subsequent Rotate/Disable/Settings calls.
            setPortal({
                ...(portal || {}),
                ...r.data.client,
                portal_enabled: true,
                portal_link: r.data.portal_link,
            });
            toast.success("Portal enabled");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    };

    const rotate = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/clients/${clientId}/portal/rotate`);
            setLink(r.data.portal_link);
            setPortal((p) => ({ ...(p || {}), portal_link: r.data.portal_link }));
            toast.success("New link generated · old link invalidated");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    };

    const disable = async () => {
        if (!window.confirm(`Disable portal access for ${buyer.name}?`)) return;
        setBusy(true);
        try {
            await api.post(`/clients/${clientId}/portal/disable`);
            toast.success("Portal disabled");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    };

    const saveSettings = async () => {
        if (!clientId) return;
        setBusy(true);
        try {
            await api.put(`/clients/${clientId}/portal/settings`, {
                contact_email: contactEmail, contact_phone: contactPhone,
                reminder_cycle_days: parseInt(cycle) || 30,
            });
            toast.success("Settings updated");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    };

    const copy = async () => {
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
            toast.success("Portal link copied to clipboard");
        } catch { toast.error("Copy failed"); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="portal-manage-modal" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h3 className="text-xl font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Portal access · {buyer.name}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">{buyer.gstin || "No GSTIN on file"}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
                </div>

                {enabled && link && (
                    <div className="mb-5 p-4 rounded-xl bg-slate-900 text-white">
                        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Magic link (share with client)</div>
                        <div className="flex items-center gap-2">
                            <input readOnly value={link}
                                   className="flex-1 bg-slate-800 text-emerald-300 font-mono text-xs px-3 py-2 rounded-lg"
                                   data-testid="portal-link-value" onClick={(e) => e.target.select()} />
                            <button onClick={copy} className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-1"
                                    data-testid="portal-copy-btn">
                                {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
                            </button>
                            <a href={link} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold inline-flex items-center gap-1">
                                <ExternalLink size={12}/> Open
                            </a>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2">
                            Anyone with this link can access {buyer.name}'s portal. If it leaks, rotate below.
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Contact email</div>
                        <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                               className="hdb-input" placeholder="orders@client.com"
                               data-testid="portal-contact-email" />
                    </label>
                    <label className="block">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Contact phone</div>
                        <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                               className="hdb-input" placeholder="+91 …" />
                    </label>
                    <label className="block md:col-span-2">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">
                            Reminder cycle (days)
                        </div>
                        <input type="number" min="1" value={cycle} onChange={(e) => setCycle(e.target.value)}
                               className="hdb-input" data-testid="portal-cycle-days" />
                        <p className="text-[10px] text-slate-400 mt-1">
                            After this many days without ordering, this client shows up in the "Reminders due" dashboard widget.
                        </p>
                    </label>
                </div>

                <div className="mt-5 flex flex-wrap gap-2 justify-end">
                    {enabled && (
                        <>
                            <button onClick={() => onOpenHistorical({ ...portal })}
                                    className="hdb-btn hdb-btn-ghost" data-testid="portal-add-historical-btn">
                                <ClipboardEdit size={13}/> Add historical invoice
                            </button>
                            <button onClick={saveSettings} disabled={busy} className="hdb-btn hdb-btn-ghost">
                                Save settings
                            </button>
                            <button onClick={rotate} disabled={busy} className="hdb-btn hdb-btn-ghost" data-testid="portal-rotate-btn">
                                <KeyRound size={13}/> Rotate link
                            </button>
                            <button onClick={disable} disabled={busy} className="hdb-btn text-red-700 border border-red-300 hover:bg-red-50">
                                <ShieldOff size={13}/> Disable portal
                            </button>
                        </>
                    )}
                    {!enabled && (
                        <button onClick={enableOrCreate} disabled={busy}
                                className="hdb-btn hdb-btn-primary" data-testid="portal-generate-btn">
                            {busy ? <><Loader2 size={13} className="animate-spin"/> Enabling…</>
                                  : <><KeyRound size={13}/> Enable &amp; generate link</>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
